export type OnboardingVideoAssets = {
  video_mp4: string;
  video_webm: string;
  captions_vtt: string;
  audio_wav: string;
  tts_only: boolean;
};

export type OnboardingAvatarConfig = {
  base_path: string;
  provider: string;
  max_clarify: number;
  dialog?: {
    max_turns: number;
    timeout_min: number;
    stt_provider: string;
  };
  script?: {
    replicas: Array<{
      id: string;
      screen: string;
      step_key?: string;
      text: string;
      trigger: string;
    }>;
    replica_count: number;
  };
};

export type OnboardingRuntimeConfig = {
  experience: 'static' | 'video' | 'avatar_script' | 'avatar_live';
  ab_enabled: boolean;
  video?: OnboardingVideoAssets;
  avatar?: OnboardingAvatarConfig;
};

const ONBOARDING_VIDEO_DISMISSED_KEY = 'ae3_onboarding_video_dismissed';

let runtimeConfig: OnboardingRuntimeConfig = {
  experience: 'static',
  ab_enabled: false,
};

export function configureOnboardingRuntime(config: Partial<OnboardingRuntimeConfig>) {
  let experience: OnboardingRuntimeConfig['experience'] = 'static';
  if (config.experience === 'video') experience = 'video';
  else if (config.experience === 'avatar_script') experience = 'avatar_script';
  else if (config.experience === 'avatar_live') experience = 'avatar_live';

  runtimeConfig = {
    ...runtimeConfig,
    ...config,
    experience,
  };
}

export function getOnboardingRuntimeConfig(): OnboardingRuntimeConfig {
  return { ...runtimeConfig };
}

export function isAvatarScriptExperience(): boolean {
  return (
    (runtimeConfig.experience === 'avatar_script' || runtimeConfig.experience === 'avatar_live') &&
    !!runtimeConfig.avatar?.script
  );
}

export function isAvatarLiveExperience(): boolean {
  return runtimeConfig.experience === 'avatar_live' && !!runtimeConfig.avatar?.dialog;
}

export function shouldShowVideoExplainer(): boolean {
  if (runtimeConfig.experience !== 'video' || !runtimeConfig.video) return false;
  try {
    return localStorage.getItem(ONBOARDING_VIDEO_DISMISSED_KEY) !== '1';
  } catch {
    return true;
  }
}

export function markVideoExplainerDismissed() {
  try {
    localStorage.setItem(ONBOARDING_VIDEO_DISMISSED_KEY, '1');
  } catch {
    // ignore storage errors
  }
}

export function buildVideoScreenMarkup(assets: OnboardingVideoAssets): string {
  const sources = assets.tts_only
    ? ''
    : `
      <source src="${assets.video_webm}" type="video/webm" />
      <source src="${assets.video_mp4}" type="video/mp4" />
    `;

  const mediaBlock = assets.tts_only
    ? `<audio id="onboarding-video-media" class="onboarding-video-audio" preload="metadata" controls>
        <source src="${assets.audio_wav}" type="audio/wav" />
      </audio>`
    : `<video id="onboarding-video-media" class="onboarding-video-player" playsinline preload="metadata" controls>
        ${sources}
        <track kind="captions" src="${assets.captions_vtt}" srclang="ru" label="Русский" default />
      </video>`;

  return `
    <section class="onboarding-screen" data-onboarding-screen="video" aria-label="Короткое видео о проводнике">
      <p class="onboarding-eyebrow">Короткое видео · ~90 секунд</p>
      <h2>Что будет дальше</h2>
      <p class="onboarding-lead">Можно посмотреть или пропустить — карточки с тем же смыслом останутся на следующем экране.</p>
      <div class="onboarding-video-wrap">
        ${mediaBlock}
      </div>
      <label class="onboarding-video-dismiss">
        <input type="checkbox" id="onboarding-video-dismiss-check" />
        <span>Не показывать это видео снова</span>
      </label>
      <div class="onboarding-actions onboarding-actions-end">
        <div class="onboarding-action-group">
          <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="skip-video">Пропустить видео</button>
          <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="video-continue">Продолжить</button>
        </div>
      </div>
    </section>
  `;
}

export function bindVideoPresenter(
  hostEl: HTMLElement,
  onComplete: (skipped: boolean) => void,
  onError: () => void
): () => void {
  const media = hostEl.querySelector<HTMLVideoElement | HTMLAudioElement>('#onboarding-video-media');
  if (!media) {
    onError();
    return () => {};
  }

  const handleError = () => onError();
  media.addEventListener('error', handleError);

  return () => {
    media.removeEventListener('error', handleError);
    void onComplete;
  };
}

export function finishVideoScreen(hostEl: HTMLElement, skipped: boolean): { skipped: boolean; dismissed: boolean } {
  const dismissCheck = hostEl.querySelector<HTMLInputElement>('#onboarding-video-dismiss-check');
  const dismissed = !!dismissCheck?.checked;
  if (dismissed) markVideoExplainerDismissed();
  const media = hostEl.querySelector<HTMLVideoElement | HTMLAudioElement>('#onboarding-video-media');
  media?.pause();
  return { skipped, dismissed };
}
